const MAX_IMAGE_BYTES = 4.5 * 1024 * 1024;
const MAX_EDGE = 3000;
const START_QUALITY = 0.92;
const MIN_QUALITY = 0.85;
const QUALITY_STEP = 0.02;
const SCALE_STEP = 0.85;
const MIN_EDGE = 1200;
const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png"];
const ALLOWED_TYPES = ["image/jpeg", "image/png"];

export const ErrUnsupportedFormat = "unsupported_format";
export const ErrImageUnreadable = "image_unreadable";
export const ErrImageTooLarge = "image_too_large";

export function isAllowedImage(file: File): boolean {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (!ALLOWED_EXTENSIONS.includes(extension)) return false;
  return file.type === "" || ALLOWED_TYPES.includes(file.type);
}

function encodeJpeg(bitmap: ImageBitmap, scale: number, quality: number): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.reject(new Error(ErrImageUnreadable));
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error(ErrImageUnreadable))),
      "image/jpeg",
      quality,
    );
  });
}

// Dimensions shrink only after quality has dropped to the floor, so legibility
// is traded away last and by the smallest amount needed.
export async function prepareImage(file: File): Promise<Blob> {
  if (!isAllowedImage(file)) throw new Error(ErrUnsupportedFormat);
  if (file.size <= MAX_IMAGE_BYTES) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new Error(ErrImageUnreadable);
  }

  try {
    const longest = Math.max(bitmap.width, bitmap.height);
    let scale = Math.min(1, MAX_EDGE / longest);
    let quality = START_QUALITY;
    for (;;) {
      const blob = await encodeJpeg(bitmap, scale, quality);
      if (blob.size <= MAX_IMAGE_BYTES) return blob;
      if (quality > MIN_QUALITY) {
        quality = Math.max(MIN_QUALITY, quality - QUALITY_STEP);
        continue;
      }
      scale *= SCALE_STEP;
      quality = MIN_QUALITY;
      if (longest * scale < MIN_EDGE) throw new Error(ErrImageTooLarge);
    }
  } finally {
    bitmap.close();
  }
}
